import React, { useEffect, useRef, useState } from "react"
import { useWatch } from "antd/es/form/Form"
import PropTypes from "prop-types"
import { Card } from "ui/Card"
import { Form, Flex, Input, Modal } from "antd"
import { useTranslation } from "react-i18next"
import { MultipleSelectWithAddItemOption } from "ui/MultipleSelect/MultipleSelectWithAddItemOption"
import { CheckableTag } from "ui/CheckableTag"
import { useDispatch, useSelector } from "react-redux"
import { getExperienceOptions, addExperienceOption } from "store/trips"
import { Text } from "ui/Typography"

export const ExperienceDetails = ({ form }) => {
  const [t] = useTranslation()
  const dispatch = useDispatch()
  const { data: experienceOptionsData, loading: isExperienceOptionsLoading } = useSelector(
    (state) => state.trips.experienceOptions
  )

  const formLanguages = useWatch("languages", form) || []
  const formSkillLevels = useWatch("skill_levels", form) || []
  const watchedIncludedItems = useWatch("included_items", form) || []
  const watchedBringItems = useWatch("bring_items", form) || []

  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false)
  const [newLanguage, setNewLanguage] = useState("")
  const [isAddingLanguage, setIsAddingLanguage] = useState(false)
  const [isSkillModalOpen, setIsSkillModalOpen] = useState(false)
  const [newSkillLevel, setNewSkillLevel] = useState("")
  const [isAddingSkillLevel, setIsAddingSkillLevel] = useState(false)
  const languageInputRef = useRef(null)
  const skillInputRef = useRef(null)

  useEffect(() => {
    dispatch(getExperienceOptions())
  }, [dispatch])

  const handleLanguageChange = (language, checked) => {
    const next = checked ? [...formLanguages, language.id] : formLanguages.filter((l) => l !== language.id)
    form.setFieldValue("languages", next)
  }

  const handleSkillLevelChange = (level, checked) => {
    const next = checked ? [...formSkillLevels, level.id] : formSkillLevels.filter((l) => l !== level.id)
    form.setFieldValue("skill_levels", next)
  }

  const handleIncludedItemsChange = (values) => {
    form.setFieldsValue({ included_items: values })
  }

  const handleBringItemsChange = (values) => {
    form.setFieldsValue({ bring_items: values })
  }

  const handleAddLanguage = () => {
    const name = newLanguage.trim()
    if (!name) return
    setIsAddingLanguage(true)
    dispatch(addExperienceOption("language", name))
      .then(() => dispatch(getExperienceOptions()))
      .then((res) => {
        const languages = res?.data?.languages || []
        const added = languages.find((l) => l.name.toLowerCase() === name.toLowerCase())
        if (added?.id) form.setFieldValue("languages", [...formLanguages, added.id])
      })
      .finally(() => {
        setNewLanguage("")
        setIsLanguageModalOpen(false)
        setIsAddingLanguage(false)
      })
  }

  const handleAddSkillLevel = () => {
    const name = newSkillLevel.trim()
    if (!name) return
    setIsAddingSkillLevel(true)
    dispatch(addExperienceOption("skill_level", name))
      .then(() => dispatch(getExperienceOptions()))
      .then((res) => {
        const skillLevels = res?.data?.skill_levels || []
        const added = skillLevels.find((s) => s.name.toLowerCase() === name.toLowerCase())
        if (added?.id) form.setFieldValue("skill_levels", [...formSkillLevels, added.id])
      })
      .finally(() => {
        setNewSkillLevel("")
        setIsSkillModalOpen(false)
        setIsAddingSkillLevel(false)
      })
  }

  return (
    <Card title={t("experiences.details.experience_details")} className="mb-3xl">
      <Form.Item name="included_items" label={t("experiences.details.labels.trip_items")}>
        <MultipleSelectWithAddItemOption
          placeholder={t("experiences.details.placeholders.select_or_type")}
          options={experienceOptionsData?.included_items}
          onChange={handleIncludedItemsChange}
          loading={isExperienceOptionsLoading}
          addItemLabel={t("experiences.details.labels.add_item")}
          notFoundLabel={t("experiences.details.labels.not_found")}
          disabledOptions={watchedBringItems}
          onAddItem={(name) => dispatch(addExperienceOption("included_item", name))}
        />
      </Form.Item>

      <Form.Item name="bring_items" label={t("experiences.details.labels.guest_items")}>
        <MultipleSelectWithAddItemOption
          placeholder={t("experiences.details.placeholders.select_or_type")}
          options={experienceOptionsData?.bring_items}
          loading={isExperienceOptionsLoading}
          onChange={handleBringItemsChange}
          addItemLabel={t("experiences.details.labels.add_item")}
          notFoundLabel={t("experiences.details.labels.not_found")}
          disabledOptions={watchedIncludedItems}
          onAddItem={(name) => dispatch(addExperienceOption("bring_item", name))}
        />
      </Form.Item>

      <Form.Item name="languages" label={t("experiences.details.labels.languages")}>
        <Flex wrap gap="small" align="center">
          {(experienceOptionsData?.languages || []).map((language) => (
            <CheckableTag
              key={language.id}
              className="mr-xs mb-xxs"
              tag={language.name}
              checked={formLanguages.includes(language.id)}
              onChange={(checked) => handleLanguageChange(language, checked)}
            />
          ))}
        </Flex>
        <div className="mt-xs">
          <Text variant="caption" type="secondary">
            {t("experiences.details.labels.not_found_language")}{" "}
            <Text variant="link" onClick={() => setIsLanguageModalOpen(true)}>
              {t("experiences.details.labels.add_language")}
            </Text>
          </Text>
        </div>
      </Form.Item>

      <Form.Item name="skill_levels" label={t("experiences.details.labels.skill_level")}>
        <Flex wrap gap="small" align="center">
          {(experienceOptionsData?.skill_levels || []).map((level) => (
            <CheckableTag
              key={level.id}
              className="mr-xs mb-xxs"
              tag={level.name}
              checked={formSkillLevels.includes(level.id)}
              onChange={(checked) => handleSkillLevelChange(level, checked)}
            />
          ))}
        </Flex>
        <div className="mt-xs">
          <Text variant="caption" type="secondary">
            {t("experiences.details.labels.not_found_skill_level")}{" "}
            <Text variant="link" onClick={() => setIsSkillModalOpen(true)}>
              {t("experiences.details.labels.add_skill_level")}
            </Text>
          </Text>
        </div>
      </Form.Item>

      <Modal
        title={t("experiences.details.labels.add_language")}
        open={isLanguageModalOpen}
        onOk={handleAddLanguage}
        onCancel={() => { setIsLanguageModalOpen(false); setNewLanguage("") }}
        okText={t("experiences.details.labels.add_item")}
        okButtonProps={{ disabled: !newLanguage.trim(), loading: isAddingLanguage }}
        afterOpenChange={(open) => { if (open) languageInputRef.current?.focus() }}
        destroyOnClose
      >
        <Input
          ref={languageInputRef}
          placeholder={t("experiences.details.placeholders.add_language")}
          value={newLanguage}
          onChange={(e) => setNewLanguage(e.target.value)}
          onPressEnter={handleAddLanguage}
        />
      </Modal>

      <Modal
        title={t("experiences.details.labels.add_skill_level")}
        open={isSkillModalOpen}
        onOk={handleAddSkillLevel}
        onCancel={() => { setIsSkillModalOpen(false); setNewSkillLevel("") }}
        okText={t("experiences.details.labels.add_item")}
        okButtonProps={{ disabled: !newSkillLevel.trim(), loading: isAddingSkillLevel }}
        afterOpenChange={(open) => { if (open) skillInputRef.current?.focus() }}
        destroyOnClose
      >
        <Input
          ref={skillInputRef}
          placeholder={t("experiences.details.placeholders.add_skill_level")}
          value={newSkillLevel}
          onChange={(e) => setNewSkillLevel(e.target.value)}
          onPressEnter={handleAddSkillLevel}
        />
      </Modal>
    </Card>
  )
}

ExperienceDetails.propTypes = {
  form: PropTypes.object.isRequired,
}
